import type { BuildingId, DecorationId } from "@engine";
import { GROUND_DEFS, SYMBOLS } from "./defs.ts";

/** The design board. One design unit = one CSS px on a 390 × 844 phone. */
export const BOARD = { w: 390, h: 844 } as const;

/** Area the surrounding forest covers, so wide screens never see an edge. */
export const WORLD = { x: -560, y: -160, w: 1510, h: 1180 } as const;

/** Extra transparent margin around the board layers so edge trees are not clipped. */
export const BOARD_MARGIN = 34;

export type Box = { x: number; y: number; w: number; h: number };
export type Art = { svg: string; box: Box };

type Plant = readonly [kind: "tree" | "pine", x: number, y: number];

/** Forest behind the walls, back to front (from the design canvas). */
const FOREST_BACK: Plant[] = [
  ["pine", 250, 158], ["tree", 45, 160], ["pine", 150, 160], ["tree", 350, 160],
  ["tree", 120, 185], ["pine", 225, 185], ["tree", 330, 185], ["pine", 70, 190],
  ["tree", 175, 190], ["tree", 280, 190], ["pine", 375, 192], ["tree", 20, 195],
  ["tree", 160, 215], ["tree", 240, 215], ["pine", 100, 225], ["pine", 300, 225],
  ["tree", 60, 230], ["tree", 200, 230], ["tree", 370, 230], ["pine", 260, 235],
  ["tree", 130, 240], ["pine", 15, 240], ["tree", 35, 255], ["pine", 230, 255],
  ["pine", 75, 262], ["tree", 160, 262], ["pine", 195, 262], ["tree", 320, 265],
  ["tree", 270, 270], ["pine", 380, 270], ["pine", 120, 272], ["tree", 215, 282],
  ["tree", 178, 286], ["tree", 85, 290], ["pine", 30, 290], ["pine", 150, 300],
  ["tree", 250, 300], ["pine", 310, 300], ["tree", 355, 300], ["tree", 118, 315],
  ["pine", 285, 318], ["tree", 52, 320], ["tree", 345, 330], ["pine", 10, 330],
  ["pine", 90, 340], ["tree", 380, 340], ["tree", 320, 346], ["tree", 30, 350],
  ["pine", 62, 358], ["tree", 372, 372], ["pine", 350, 378], ["tree", 40, 388],
  ["pine", 14, 398], ["tree", 380, 412], ["tree", 14, 432],
];

/** Trees between the front wall and the harbour. */
const FOREST_FRONT: Plant[] = [
  ["tree", 12, 482], ["tree", 340, 520], ["pine", 35, 522], ["tree", 320, 555],
  ["tree", 52, 556], ["pine", 18, 566], ["tree", 140, 576], ["tree", 272, 580],
];

/** Woodland along the bottom of the board. */
const FOREST_BOTTOM: Plant[] = [
  ["tree", 20, 642], ["pine", 140, 650], ["tree", 225, 652], ["tree", 55, 656],
  ["pine", 95, 662], ["tree", 182, 666], ["tree", 150, 702], ["pine", 40, 706],
  ["tree", 100, 716], ["pine", 200, 722], ["tree", 12, 760], ["pine", 62, 772],
  ["tree", 122, 768], ["tree", 182, 782], ["pine", 225, 800], ["tree", 35, 820],
  ["tree", 140, 830],
];

const FLOWERS: Array<readonly [number, number]> = [
  [90, 430], [305, 428], [240, 426], [175, 388], [100, 478], [172, 522],
  [160, 536], [60, 625], [160, 625], [250, 565],
];
const TUFTS: Array<readonly [number, number]> = [
  [120, 440], [282, 440], [228, 396], [160, 396], [95, 470], [178, 545],
  [30, 600], [262, 560], [210, 640],
];
const ROCKS: Array<readonly [number, number]> = [[60, 500], [248, 590], [372, 478]];

const plant = ([kind, x, y]: Plant): string =>
  `<use href="#${kind === "tree" ? "lvTree" : "lvPine"}" x="${x}" y="${y}"/>`;

const at = (sym: string, list: ReadonlyArray<readonly [number, number]>): string =>
  list.map(([x, y]) => `<use href="#${sym}" x="${x}" y="${y}"/>`).join("");

function wallRun(points: string): string {
  return `<g fill="none" stroke-linejoin="round" stroke-linecap="round">
    <path d="${points}" transform="translate(0,6)" stroke="#2e2418" stroke-width="11"/>
    <path d="${points}" stroke="#2e2418" stroke-width="11"/>
    <path d="${points}" transform="translate(0,5)" stroke="#7d7468" stroke-width="8"/>
    <path d="${points}" stroke="#cfc6b6" stroke-width="8"/>
    <path d="${points}" stroke="#efe8dc" stroke-width="3" stroke-dasharray="5 5" stroke-linecap="butt"/>
  </g>`;
}

const RIVER_CORE = "M398,500 C352,560 318,600 300,640 C285,675 255,720 238,850";
const RIVER_WIDE = `M${WORLD.x + WORLD.w + 10},150 C760,250 520,400 ${RIVER_CORE.slice(1)} L228,${WORLD.y + WORLD.h + 10}`;

function river(path: string, close: string): string {
  return `
    <path d="${path} ${close}" fill="#41b3e8" stroke="#f1dc9e" stroke-width="10"/>
    <path d="M398,560 C366,612 346,652 332,700 C320,742 308,790 302,850 L300,${WORLD.y + WORLD.h + 10} L${WORLD.x + WORLD.w + 10},${WORLD.y + WORLD.h + 10} L${WORLD.x + WORLD.w + 10},250 C800,330 560,470 398,560 Z" fill="#2a94d0"/>
    <path d="${path}" transform="translate(7,4)" fill="none" stroke="#9ae6ff" stroke-width="2.6" opacity="0.85"/>`;
}

function svgDoc(box: Box, body: string, defs = ""): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="${box.x} ${box.y} ${box.w} ${box.h}" width="${box.w}" height="${box.h}"><defs>${GROUND_DEFS}${SYMBOLS}${defs}</defs>${body}</svg>`;
}

const boardBox: Box = {
  x: -BOARD_MARGIN,
  y: -BOARD_MARGIN,
  w: BOARD.w + BOARD_MARGIN * 2,
  h: BOARD.h + BOARD_MARGIN * 2,
};

/** Deterministic scatter for the surrounding forest. */
function surroundTrees(): string {
  const out: string[] = [];
  let seed = 11;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const inBoard = (x: number, y: number) => x > -18 && x < BOARD.w + 18 && y > -6 && y < BOARD.h + 40;
  const inRiver = (x: number, y: number) => {
    // Below the board the river runs straight down from x ≈ 228.
    if (y > BOARD.h - 10 && x > 214) return true;
    // Rough band following the river beyond the board's right edge.
    if (x < BOARD.w - 20) return false;
    const centre = 520 - (x - 398) * 0.62;
    return y > centre - 40;
  };
  const step = 27;
  for (let y = WORLD.y + 10; y < WORLD.y + WORLD.h + 30; y += step * 0.86) {
    for (let x = WORLD.x + 10; x < WORLD.x + WORLD.w; x += step) {
      const jx = x + (rand() - 0.5) * 16;
      const jy = y + (rand() - 0.5) * 12;
      if (inBoard(jx, jy) || inRiver(jx, jy)) continue;
      if (rand() < 0.12) continue;
      out.push(plant([rand() < 0.42 ? "pine" : "tree", Math.round(jx), Math.round(jy)]));
    }
  }
  return out.join("");
}

/** Grass, river and forest beyond the board. Rendered at lower resolution. */
export function surroundArt(): Art {
  const box: Box = { ...WORLD };
  const body = `
    <rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" fill="url(#lvGrass)"/>
    <rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" fill="#000" filter="url(#lvPatch)" opacity="0.7"/>
    <rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" fill="#000" filter="url(#lvGrain)" opacity="0.9"/>
    ${river(RIVER_WIDE, `L${box.x + box.w + 10},${box.y + box.h + 10} L${box.x + box.w + 10},150 Z`)}
    ${surroundTrees()}`;
  return { svg: svgDoc(box, body), box };
}

/** Everything on the board that sits behind the buildings. */
export function boardBackArt(): Art {
  const body = `
    <clipPath id="lvBoardClip"><rect x="0" y="0" width="${BOARD.w}" height="${BOARD.h}"/></clipPath>
    <g clip-path="url(#lvBoardClip)">
      <rect x="0" y="0" width="${BOARD.w}" height="${BOARD.h}" fill="url(#lvGrass)"/>
      <rect x="0" y="0" width="${BOARD.w}" height="${BOARD.h}" fill="#000" filter="url(#lvPatch)" opacity="0.7"/>
      <rect x="0" y="0" width="${BOARD.w}" height="${BOARD.h}" fill="#000" filter="url(#lvGrain)" opacity="0.9"/>
      ${river(RIVER_CORE, "L398,850 Z")}
      <path d="M332,560 q6,-4 12,0 M352,600 q6,-4 12,0 M300,700 q6,-4 12,0 M340,740 q6,-4 12,0 M288,780 q6,-4 12,0" fill="none" stroke="#e6fbff" stroke-width="1.8" stroke-linecap="round"/>
    </g>
    <g filter="url(#lvFeather)" fill="none" stroke-linejoin="round" stroke-linecap="round">
      <path d="M195,413 L257,455 L195,497 L133,455 Z M195,413 L195,342 M257,455 L352,452 M195,497 L195,600 M133,455 L38,452 M195,600 C160,600 132,596 108,598 M195,600 C232,602 262,606 290,614" stroke="#c99a5e" stroke-width="16"/>
      <path d="M195,413 L257,455 L195,497 L133,455 Z M195,413 L195,342 M257,455 L352,452 M195,497 L195,600 M133,455 L38,452 M195,600 C160,600 132,596 108,598 M195,600 C232,602 262,606 290,614" stroke="#e4c08a" stroke-width="9"/>
    </g>
    <g fill="#b48850">
      <circle cx="190" cy="370" r="1.4"/><circle cx="200" cy="388" r="1.2"/><circle cx="300" cy="451" r="1.4"/><circle cx="80" cy="456" r="1.3"/><circle cx="198" cy="540" r="1.4"/><circle cx="230" cy="440" r="1.2"/><circle cx="160" cy="476" r="1.3"/><circle cx="150" cy="598" r="1.4"/><circle cx="250" cy="606" r="1.3"/>
    </g>
    ${at("lvFlower", FLOWERS)}${at("lvTuft", TUFTS)}${at("lvRock", ROCKS)}
    ${FOREST_BACK.map(plant).join("")}
    ${wallRun("M23,450 L195,332 L367,450")}
    <use href="#lvPillar" x="195" y="334"/><use href="#lvPillar" x="109" y="393"/><use href="#lvPillar" x="281" y="393"/>
    <use href="#lvPillar" x="23" y="452"/><use href="#lvPillar" x="367" y="452"/>`;
  return { svg: svgDoc(boardBox, body), box: boardBox };
}

/** Front wall, gate and the trees that overlap the buildings. */
export function boardFrontArt(): Art {
  const body = `
    ${wallRun("M23,450 L195,568 L367,450")}
    <use href="#lvPillar" x="109" y="511"/><use href="#lvPillar" x="281" y="511"/>
    <use href="#lvGate" x="195" y="570"/>
    ${FOREST_FRONT.map(plant).join("")}
    ${FOREST_BOTTOM.map(plant).join("")}`;
  return { svg: svgDoc(boardBox, body), box: boardBox };
}

/* ---------- Buildings ---------- */

export type PlacedArt = Art & { x: number; y: number };

const TANK_EMBLEMS: Record<string, { color: string; scale: string; emblem: string }> = {
  crystalTankViralz: {
    color: "#ff4fa3",
    scale: "scale(0.95,1.12)",
    emblem: `<g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round">
      <path d="M0,-37 L0,-46" fill="none" stroke-width="2"/>
      <path d="M2.5,-56 L-3.5,-47 L0,-47 L-2.5,-40 L4.5,-49.5 L1,-49.5 Z" fill="#ff4fa3"/></g>`,
  },
  crystalTankTikwave: {
    color: "#22c7e6",
    scale: "scale(1.25,0.88)",
    emblem: `<g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round">
      <path d="M-8,-27 C-9,-37 4,-40 7,-32 C4,-34.5 0,-33 1,-29 C-2,-27 -5,-26 -8,-27 Z" fill="#22c7e6"/>
      <path d="M-5,-30 C-4,-34 0,-36 3,-34.5" fill="none" stroke="#e6fbff" stroke-width="1.4"/></g>`,
  },
  crystalTankGoGrow: {
    color: "#45d65a",
    scale: "",
    emblem: `<g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round">
      <path d="M0,-33 L0,-42" fill="none" stroke="#2e7a2e" stroke-width="2.2"/>
      <path d="M0,-38 C-3,-44 -9,-45 -11,-41 C-8,-37 -3,-36 0,-38 Z" fill="#45d65a"/>
      <path d="M0,-41 C2,-48 9,-50 11,-46 C9,-41 4,-39 0,-41 Z" fill="#6ae67a"/></g>`,
  },
  crystalTankRihla: {
    color: "#ff9a2e",
    scale: "",
    emblem: `<g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round">
      <path d="M-3,-32 L3,-32 L1.5,-35 L-1.5,-35 Z" fill="#9aa1ad"/>
      <circle cx="0" cy="-41" r="6" fill="#ff9a2e"/>
      <path d="M-6,-41 L6,-41 M0,-47 C-3,-44 -3,-38 0,-35 C3,-38 3,-44 0,-47" fill="none" stroke="#a3500a" stroke-width="1.1"/>
      <path d="M-3.5,-44 Q-2,-46 0,-46.5" fill="none" stroke="#ffe1b8" stroke-width="1.4"/></g>`,
  },
  crystalTankRevenue: {
    color: "#8f63ff",
    scale: "scale(1.05,1)",
    emblem: `<g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round">
      <ellipse cx="0" cy="-33" rx="6" ry="2.6" fill="#c98a0c"/><ellipse cx="0" cy="-34.5" rx="6" ry="2.6" fill="#ffc928"/>
      <ellipse cx="1" cy="-37.5" rx="5" ry="2.2" fill="#c98a0c"/><ellipse cx="1" cy="-39" rx="5" ry="2.2" fill="#ffc928"/>
      <ellipse cx="0" cy="-42" rx="4" ry="1.8" fill="#c98a0c"/><ellipse cx="0" cy="-43.2" rx="4" ry="1.8" fill="#ffe27a"/></g>`,
  },
};

const SINGLE: Partial<Record<BuildingId, { sym: string; box: Box; x: number; y: number }>> = {
  townHall: { sym: "lvKeep", box: { x: -62, y: -140, w: 124, h: 178 }, x: 195, y: 455 },
  goldMine: { sym: "lvMine", box: { x: -42, y: -42, w: 88, h: 70 }, x: 255, y: 405 },
  workshop: { sym: "lvWork", box: { x: -36, y: -50, w: 72, h: 70 }, x: 135, y: 405 },
  barracks: { sym: "lvBarracks", box: { x: -42, y: -52, w: 76, h: 72 }, x: 62, y: 452 },
  watchtower: { sym: "lvWatch", box: { x: -24, y: -108, w: 50, h: 122 }, x: 335, y: 452 },
  lab: { sym: "lvLab", box: { x: -31, y: -51, w: 65, h: 69 }, x: 135, y: 505 },
};

const TANK_SPOTS: Partial<Record<BuildingId, { x: number; y: number }>> = {
  crystalTankViralz: { x: 300, y: 460 },
  crystalTankTikwave: { x: 278, y: 475 },
  crystalTankGoGrow: { x: 256, y: 490 },
  crystalTankRihla: { x: 234, y: 505 },
  crystalTankRevenue: { x: 212, y: 520 },
};

export const HUT_SPOTS = [
  { x: 80, y: 600 },
  { x: 112, y: 614 },
] as const;

const TANK_BOX: Box = { x: -19, y: -60, w: 40, h: 72 };
const HUT_BOX: Box = { x: -17, y: -39, w: 37, h: 49 };

/** Art for one building, drawn around its ground point, plus where it stands. */
export function buildingArt(id: BuildingId): PlacedArt | null {
  const single = SINGLE[id];
  if (single) {
    return { svg: svgDoc(single.box, `<use href="#${single.sym}"/>`), box: single.box, x: single.x, y: single.y };
  }
  const tank = TANK_EMBLEMS[id];
  const spot = TANK_SPOTS[id];
  if (tank && spot) {
    const body = `<use href="#lvTank" transform="${tank.scale}" color="${tank.color}" style="color:${tank.color}"/>${tank.emblem}`;
    return { svg: svgDoc(TANK_BOX, body), box: TANK_BOX, ...spot };
  }
  if (id === "builderHut") {
    return { svg: svgDoc(HUT_BOX, `<use href="#lvHut"/>`), box: HUT_BOX, ...HUT_SPOTS[0] };
  }
  return null;
}

/** A short run of wall with a pillar, for the Walls panel thumbnail. */
export function wallThumbArt(): Art {
  const box: Box = { x: -40, y: -34, w: 80, h: 56 };
  const body = `${wallRun("M-30,8 L0,-12 L30,8")}<use href="#lvPillar" x="0" y="-10"/><use href="#lvPillar" x="-30" y="10"/><use href="#lvPillar" x="30" y="10"/>`;
  return { svg: svgDoc(box, body), box };
}

/** Dock and sailing boat on the river (scenic, tappable). */
export function harbourArt(): PlacedArt {
  const box: Box = { x: -46, y: -30, w: 94, h: 62 };
  const body = `<g transform="translate(-322,-636)">
    <g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round">
      <path d="M286,610 L330,636 L322,641 L278,615 Z" fill="#c48a4a"/>
      <path d="M278,615 L322,641 L322,644 L278,618 Z" fill="#8f5d2b"/>
      <path d="M287,620 L295,615 M296,625 L304,620 M305,631 L313,626" fill="none" stroke="#8f5d2b" stroke-width="1"/>
      <path d="M322,641 L322,650 M330,636 L330,645" fill="none" stroke-width="2.4"/>
    </g>
    <g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round">
      <ellipse cx="349" cy="662" rx="17" ry="4" fill="#1f6fa3" opacity="0.5" stroke="none"/>
      <path d="M332,652 Q348,666 366,652 L361,649 L337,649 Z" fill="#a56a35"/>
      <path d="M349,649 L349,624" fill="none" stroke-width="1.8"/>
      <path d="M350.5,626 L364,645 L350.5,645 Z" fill="#ffffff"/>
      <path d="M347.5,630 L338,645 L347.5,645 Z" fill="#ffe9b0"/>
    </g></g>`;
  return { svg: svgDoc(box, body), box, x: 322, y: 636 };
}

/** Walls hit areas in board units (left run, right run, gate). */
export const WALL_HIT_LINES: Array<readonly [number, number, number, number]> = [
  [23, 450, 195, 568],
  [195, 568, 367, 450],
  [23, 450, 195, 332],
  [195, 332, 367, 450],
];

/* ---------- Collect bubbles ---------- */

const BUBBLE_BOX: Box = { x: -17, y: -29, w: 34, h: 32 };

export function goldBubbleArt(): Art {
  const body = `<use href="#lvBubble"/><g transform="translate(0,-16)">
    <circle cx="0" cy="1" r="6.5" fill="#c98a0c" stroke="#2e2418" stroke-width="1.4"/>
    <circle cx="0" cy="0" r="6.5" fill="#ffc928" stroke="#2e2418" stroke-width="1.4"/>
    <circle cx="0" cy="0" r="3.6" fill="#ffe27a"/></g>`;
  return { svg: svgDoc(BUBBLE_BOX, body), box: BUBBLE_BOX };
}

export function crystalBubbleArt(): Art {
  const body = `<use href="#lvBubble"/><g transform="translate(0,-16)">
    <path d="M0,-7 L6,-2 L0,7 L-6,-2 Z" fill="#a35cff" stroke="#2e2418" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M0,-7 L2.4,-2 L0,7 L-2.4,-2 Z" fill="#d3b0ff"/></g>`;
  return { svg: svgDoc(BUBBLE_BOX, body), box: BUBBLE_BOX };
}

export function checkBubbleArt(): Art {
  const body = `<use href="#lvBubble"/>
    <path d="M-6,-16 L-2,-12 L6,-21" fill="none" stroke="#2f9e2a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`;
  return { svg: svgDoc(BUBBLE_BOX, body), box: BUBBLE_BOX };
}

export const BUBBLE_SPOTS = {
  gold: { x: 255, y: 366 },
  crystal: { x: 300, y: 398 },
  check: { x: 80, y: 566 },
} as const;

/* ---------- Goblin raid ---------- */

export function goblinArt(): Art {
  const box: Box = { x: -12, y: -26, w: 24, h: 30 };
  const body = `<g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round">
    <ellipse cx="1" cy="1" rx="7" ry="3" fill="#0e1a0c" opacity="0.3" stroke="none"/>
    <path d="M-3,0 L-3,-5 M3,0 L3,-5" fill="none" stroke="#3f7a2a" stroke-width="2.4"/>
    <path d="M-5,-5 L5,-5 L4,-13 L-4,-13 Z" fill="#8a5a2c"/>
    <path d="M-5,-21 L-11,-24 L-6,-17 Z M5,-21 L11,-24 L6,-17 Z" fill="#6cc43f"/>
    <circle cx="0" cy="-18" r="5.6" fill="#7bd34a"/>
    <circle cx="-2" cy="-19" r="1.3" fill="#fff36b" stroke="none"/>
    <circle cx="2" cy="-19" r="1.3" fill="#fff36b" stroke="none"/>
    <path d="M-2,-15.5 Q0,-14 2,-15.5" fill="none" stroke-width="1"/></g>`;
  return { svg: svgDoc(box, body), box };
}

export function sackArt(): Art {
  const box: Box = { x: -9, y: -14, w: 18, h: 16 };
  const body = `<g stroke="#2e2418" stroke-width="1.3" stroke-linejoin="round">
    <path d="M-5,0 C-8,-3 -6,-9 -2,-10 L-3,-12 L3,-12 L2,-10 C6,-9 8,-3 5,0 Z" fill="#c9a26a"/>
    <circle cx="0" cy="-5" r="2.2" fill="#ffc928"/></g>`;
  return { svg: svgDoc(box, body), box };
}

export function smokeArt(): Art {
  const box: Box = { x: -8, y: -30, w: 20, h: 32 };
  const body = `<circle cx="0" cy="-8" r="7" fill="#6d6a6a" opacity="0.75"/>
    <circle cx="5" cy="-16" r="6" fill="#8a8686" opacity="0.65"/>
    <circle cx="-2" cy="-24" r="5" fill="#a5a1a0" opacity="0.5"/>`;
  return { svg: svgDoc(box, body), box };
}

export function fireArt(): Art {
  const box: Box = { x: -8, y: -18, w: 16, h: 20 };
  const body = `<g stroke="#2e2418" stroke-width="1.2" stroke-linejoin="round">
    <path d="M0,0 C-7,-2 -7,-10 -2,-16 C-2,-11 1,-10 2,-14 C6,-9 7,-2 0,0 Z" fill="#ff7a1f"/>
    <path d="M0,-1.5 C-3,-3 -3,-7 0,-9 C1,-6 3,-5 2,-3 Z" fill="#ffd34d" stroke="none"/></g>`;
  return { svg: svgDoc(box, body), box };
}

export function rubbleArt(): Art {
  const box: Box = { x: -8, y: -5, w: 16, h: 10 };
  const body = `<g stroke="#2e2418" stroke-width="1">
    <path d="M-6,0 L-3,-3 L0,0 Z M1,1 L4,-3 L7,1 Z M-2,3 L0,0 L3,3 Z" fill="#a79f92"/></g>`;
  return { svg: svgDoc(box, body), box };
}

export const RAID_LAYOUT = {
  smoke: [[109, 505], [281, 388], [236, 545]],
  fire: [[113, 514], [279, 396]],
  rubble: [[104, 520], [286, 400], [232, 550]],
  goblins: [[244, 398], [292, 408], [98, 530], [122, 545], [250, 560], [305, 532], [62, 500]],
  sacks: [[252, 395], [106, 528], [258, 557]],
} as const;

/* ---------- Decorations (same drawings as the shop cards) ---------- */

export const DECO_BOX: Box = { x: 0, y: 0, w: 60, h: 54 };

export const DECO_BODY: Record<DecorationId, string> = {
  crescentLantern: `
    <ellipse cx="30" cy="48" rx="14" ry="4" fill="#d8c9a6"/>
    <path d="M28,48 L28,22 L32,22 L32,48 Z" fill="#4a4f5a" stroke="#2e2418" stroke-width="1.8"/>
    <path d="M22,22 L38,22 L36,10 L24,10 Z" fill="#ffd34d" stroke="#2e2418" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M21,10 L39,10 L30,3 Z" fill="#2b55bb" stroke="#2e2418" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M40,6 A5,5 0 1 0 40,15 A4,4 0 1 1 40,6 Z" fill="#ffc928" stroke="#2e2418" stroke-width="1.2"/>`,
  flowerBed: `
    <path d="M8,38 L30,26 L52,38 L30,50 Z" fill="#8a5a2c" stroke="#2e2418" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M12,37 L30,28 L48,37 L30,46 Z" fill="#5e3b1c"/>
    <circle cx="22" cy="35" r="3.6" fill="#ff7aa8" stroke="#2e2418" stroke-width="1.2"/>
    <circle cx="30" cy="31" r="3.6" fill="#ffe14a" stroke="#2e2418" stroke-width="1.2"/>
    <circle cx="38" cy="36" r="3.6" fill="#ffffff" stroke="#2e2418" stroke-width="1.2"/>
    <circle cx="30" cy="40" r="3.6" fill="#a35cff" stroke="#2e2418" stroke-width="1.2"/>`,
  fountain: `
    <ellipse cx="30" cy="40" rx="22" ry="9" fill="#cfc6b6" stroke="#2e2418" stroke-width="1.8"/>
    <ellipse cx="30" cy="38" rx="17" ry="6" fill="#41b3e8"/>
    <path d="M27,38 L27,20 L33,20 L33,38 Z" fill="#e6dfd2" stroke="#2e2418" stroke-width="1.6"/>
    <path d="M30,18 C24,12 20,16 18,24 M30,18 C36,12 40,16 42,24" fill="none" stroke="#7fd6ff" stroke-width="2.4" stroke-linecap="round"/>
    <circle cx="30" cy="16" r="3" fill="#9ae6ff"/>`,
  datePalm: `
    <ellipse cx="30" cy="49" rx="12" ry="3.5" fill="#d8c9a6"/>
    <path d="M29,49 C30,38 27,26 30,16" fill="none" stroke="#2e2418" stroke-width="5" stroke-linecap="round"/>
    <path d="M29,49 C30,38 27,26 30,16" fill="none" stroke="#b57a3c" stroke-width="3" stroke-linecap="round"/>
    <path d="M30,16 C22,10 14,12 10,20 C17,17 24,17 30,16 Z M30,16 C38,10 46,12 50,20 C43,17 36,17 30,16 Z M30,16 C26,8 20,4 14,6 C21,8 26,12 30,16 Z M30,16 C34,8 40,4 46,6 C39,8 34,12 30,16 Z" fill="#3aa13c" stroke="#2e2418" stroke-width="1.5" stroke-linejoin="round"/>
    <circle cx="27" cy="19" r="2" fill="#c97a1c"/>
    <circle cx="32" cy="20" r="2" fill="#c97a1c"/>`,
  villageBanner: `
    <path d="M16,50 L16,6" fill="none" stroke="#2e2418" stroke-width="3.4" stroke-linecap="round"/>
    <path d="M17,8 L44,8 L38,16 L44,24 L17,24 Z" fill="#2fd3e8" stroke="#2e2418" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M24,16 L33,16" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round"/>
    <circle cx="16" cy="5" r="2.6" fill="#ffc928" stroke="#2e2418" stroke-width="1.2"/>`,
  gardenBench: `
    <path d="M10,34 L30,24 L50,34 L30,44 Z" fill="#c48a4a" stroke="#2e2418" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M10,34 L30,44 L30,48 L10,38 Z" fill="#8f5d2b" stroke="#2e2418" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M30,44 L50,34 L50,38 L30,48 Z" fill="#6b4226" stroke="#2e2418" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M14,39 L14,47 M46,39 L46,47" fill="none" stroke="#2e2418" stroke-width="2.4" stroke-linecap="round"/>
    <path d="M18,32 L34,24 M24,35 L40,27" fill="none" stroke="#8f5d2b" stroke-width="1.2"/>`,
};

export function decorationArt(id: DecorationId): Art {
  return { svg: svgDoc(DECO_BOX, DECO_BODY[id]), box: DECO_BOX };
}

/** Default spot for a newly bought decoration; the player can drag it anywhere. */
export const DECO_SPOTS: Record<DecorationId, { x: number; y: number }> = {
  fountain: { x: 195, y: 532 },
  crescentLantern: { x: 236, y: 588 },
  flowerBed: { x: 168, y: 632 },
  datePalm: { x: 92, y: 492 },
  villageBanner: { x: 112, y: 560 },
  gardenBench: { x: 256, y: 632 },
};

/** Map size of a decoration (shop art is 60 × 54). */
export const DECO_SCALE = 0.5;

/** Full standalone SVG for an <img> thumbnail of a building or decoration. */
export function thumbnailSvg(art: Art): string {
  return art.svg;
}
